<template>
  <input
    v-model="query"
    class="od-input tw:w-full"
    placeholder="Select one"
    @keydown.up="action.up"
    @keydown.down="action.down"
    @keydown.enter.prevent="action.enter"
    @focus="selectOnFocus"
    @blur="onBlur"
    :required="props.required"
  />

  <div
    ref="combobox"
    v-show="show && airports.length && query.length > 0"
    class="od-combobox"
  >
    <div class="od-combo-container">
      <div
        class="od-combo-item"
        :class="i === counter && 'tw:!bg-stone-200'" 
        v-for="item, i of airports"
        @mousedown="select(item)"
      >
        {{ format(item) }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Fuse from 'fuse.js'
import { useAutocompletionHelper } from '~/composables/useAutocompletionHelper'
import { axios, sleep } from '~/composables/general'

export type Airport = {
  name: string,
  iata: string,
  city: string,
  state: string,
  country?: string
}

const fuse = ref<Fuse<Airport>>()
</script>


<script lang="ts" setup>
import { ref, nextTick, watch, onMounted } from 'vue'
// import VInput from '../base/form/input/VInput.vue'
import { useLocalStorage, watchDebounced } from '@vueuse/core'
// import Countries from '~/resources/countries.json'

const AIRPORT_PATTERN = /^[A-Z]{3}\s-/

const props = defineProps<{ required?: boolean }>()

const model = defineModel<Airport>()

const query = ref('')
const skip = ref(false)
const show = ref(false)
const airports = ref(new Array<Airport>)
const list = useLocalStorage<Airport[]>('list:airports:v3', [])

const format = (item: Airport) => `${item.iata} - ${item.city}, ${item.country ?? item.name}`

format.query = (item: Airport) => `${item.iata} - ${item.name}`

const select = (airport: Airport) => {
  model.value = airport
  skip.value = true
  query.value = format.query(airport)
  airports.value = []

  nextTick(() => skip.value = false)
}

const { counter, ...action } = useAutocompletionHelper(airports, query, select)

const onBlur = async () => {
  await sleep(100)

  if (!model.value || format.query(model.value) !== query.value) {
    query.value = ''
    model.value = undefined
  }

  show.value = false
}

const search = async (search: string) => {
  const fallback = {
    $or: [
      { city: 'Tokyo' },
      { city: 'Sydney' },
      { city: 'New York' },
      { city: 'Paris' },
      { city: 'Ho Chi Minh' }
    ]
  }

  const isFinal = AIRPORT_PATTERN.test(search)

  if (isFinal) {
    return
  }

  if (fuse.value && search.length < 15) {
    return fuse.value.search(search || fallback, { limit: 7 }).map(item => item.item)
  }

  const { data } = await axios.get<Airport[]>('/public/airport', {
    params: { search }
  })

  return data
}

const selectOnFocus = (event: Event) => {
  show.value = true
  const isFinal = AIRPORT_PATTERN.test(query.value)

  isFinal && sleep(50).then(() => (event.target as HTMLInputElement).select())
}

watch(() => query.value, (text, old) => {
  const isFinal = AIRPORT_PATTERN.test(old)

  if (isFinal && old.length -1 === text.length) {
    query.value = ''
  }
})

watchDebounced(() => query.value, async (term) => {
  if (skip.value) {
    return
  }

  const data = await search(term)

  if (data) {
    airports.value = data
  }
}, { debounce: 100 })


watch(list, (airports) => {
  if (! airports.length) {
    return
  }

  fuse.value ??= new Fuse(airports, {
    keys: ['name', 'iata', 'state', 'city', 'country'],
    includeScore: true,
    threshold: 0.3,
  })
}, { immediate: true })

const registerAirportData = async () => {
  if (list.value.length) {
    return airports.value = await search('') || []
  }

  const { data } = await axios.get<string>('/public/airport/text')

  const regex = /international/i

  list.value = data.split('\n').map((row): Airport => {
    const [iata, name, city, state] = row.split('|')

    return { iata, name, city, state } as any
  })
  .sort(
    (a, b) => +regex.test(b.name) - +regex.test(a.name)
  )
}

onMounted(() => {
  if (model.value) {
    query.value = `${model.value.iata} - ${model.value.name}`
  }

  setTimeout(registerAirportData, 5_000)
})
</script>

<style scoped>
@reference '~/style.css';

.od-combobox {
  @apply tw:absolute tw:z-10 tw:bg-white tw:shadow-xl tw:rounded tw:border tw:border-gray-200 tw:mt-1;
}

.od-combo-container {
  @apply tw:grid tw:gap-1 tw:p-1 tw:overflow-y-auto tw:text-left;
}

.od-combo-item {
  @apply tw:cursor-pointer tw:px-2 tw:py-1 tw:hover:bg-stone-100 tw:rounded;
}
</style>